import { boolean, discriminatedUnion, enum as enumFactory, int, literal, null as nullFactory, number, strictObject, string, union } from "../../lazy-zod.js";
import { profileWorkingDirectorySchema } from "../protocol/profile.js";
import { executionContextCompletionSchema } from "../protocol/execution-context.js";
import { agentControllerConversationRefSchema } from "./conversation-contract.js";
import { runtimeTransportModeSchema } from "./controller-contract.js";
import { dispatchExecutionSchema } from "./conversation-lifecycle.js";
import { bootstrapTemplateFactsSchema } from "./conversation-execution-state.js";
import { projectLocalConnectionFailureReasonCodeSchema } from "../../public-agent-failure-reason.js";
import { openMeldSpaceEgressActionSchema } from "../space-mesh/openmeld-space-action.js";
//#region src/schemas/daemon/task-result.ts
const OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION = 4;
const OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION_PREVIOUS = 3;
const OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION_LEGACY = 2;
const OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION_OLDEST = 1;
const openMeldDaemonTaskResultStatusSchema = enumFactory([
	"success",
	"partial_success",
	"failed"
]);
const openMeldDaemonTaskResultErrorTypeSchema = enumFactory([
	"config",
	"network",
	"adapter",
	"policy",
	"timeout",
	"internal",
	"auth"
]);
const openMeldDaemonProviderAccessEvidenceProviderSchema = enumFactory([
	"claude-code",
	"codex",
	"opencode",
	"openclaw"
]);
const openMeldDaemonProviderAccessEvidenceReasonCodeSchema = enumFactory([
	"usage_exhausted",
	"provider_account.capacity_exhausted",
	"provider_account.organization_access_disabled",
	"provider_account.subscription_access_unavailable"
]);
const OPENMELD_ORIGINAL_ERROR_MAX_LENGTH = 2e3;
const ORIGINAL_ERROR_DIAGNOSTIC_FIELD_PATTERN = /(?:^|\n)\s*(?:args|command|cwd|raw(?:[_ ]payload)?|stack|stderr|stdout)\s*:/i;
const ORIGINAL_ERROR_LOCAL_PATH_PATTERN = /(?:^|[\s('"`])(?:\/(?:Users|home|private|tmp|var)\/|[a-z]:\\)/i;
const ORIGINAL_ERROR_RAW_PAYLOAD_PATTERN = /^\s*[[{]/;
const ORIGINAL_ERROR_SECRET_PATTERN = /(?:\bsk-[a-z0-9_-]{16,}|\b(?:api[_ -]?key|authorization|access[_ -]?token)\s*[:=]\s*\S+)/i;
const ORIGINAL_ERROR_STACK_TRACE_PATTERN = /(?:^|\n)\s*(?:at\s+\S+|Error:.*\n\s*at\s+)/;
function isOriginalErrorSafeForMessageDetails(value) {
	return !(ORIGINAL_ERROR_DIAGNOSTIC_FIELD_PATTERN.test(value) || ORIGINAL_ERROR_LOCAL_PATH_PATTERN.test(value) || ORIGINAL_ERROR_RAW_PAYLOAD_PATTERN.test(value) || ORIGINAL_ERROR_SECRET_PATTERN.test(value) || ORIGINAL_ERROR_STACK_TRACE_PATTERN.test(value));
}
const openMeldDaemonOriginalErrorSchema = string().trim().min(1).max(OPENMELD_ORIGINAL_ERROR_MAX_LENGTH).refine(isOriginalErrorSafeForMessageDetails, { message: "original error must not contain diagnostics, sensitive values, or local paths" });
const openMeldDaemonProviderAccessEvidenceSourceSchema = literal("wake_failure");
const openMeldDaemonProviderAccessEvidenceSchema = strictObject({
	observedAtMs: number().int().nonnegative(),
	provider: openMeldDaemonProviderAccessEvidenceProviderSchema,
	reasonCode: openMeldDaemonProviderAccessEvidenceReasonCodeSchema,
	source: openMeldDaemonProviderAccessEvidenceSourceSchema
});
const openMeldDaemonTaskResultSpaceActionProvenanceSchema = enumFactory([
	"cli_action",
	"direct",
	"local_repair",
	"controller_repair"
]);
const openMeldDaemonTaskResultSpaceActionFailureRepairModeSchema = enumFactory([
	"required_check",
	"local_synthetic",
	"controller_roundtrip"
]);
const openMeldDaemonTaskResultSpaceActionCarrierKindSchema = enumFactory([
	"fenced_block",
	"invalid_json",
	"none",
	"trailing_json"
]);
const openMeldDaemonTaskResultSpaceActionFailureDiagnosticsSchema = strictObject({
	carrierKind: openMeldDaemonTaskResultSpaceActionCarrierKindSchema,
	invalidOutputPreview: union([string().min(1), nullFactory()]),
	publicFailureReason: union([string().min(1), nullFactory()]).optional().default(null),
	repairMode: openMeldDaemonTaskResultSpaceActionFailureRepairModeSchema,
	spaceActionCandidatePresent: boolean(),
	spaceActionParseError: union([string().min(1), nullFactory()])
});
const openMeldDaemonTaskResultOpenClawGatewayVisibilityStatusSchema = enumFactory([
	"missing_after_restart",
	"missing_before_restart",
	"visible",
	"visible_after_restart"
]);
const openMeldDaemonTaskResultOpenClawGatewayVisibilitySchema = strictObject({
	agentId: string().min(1),
	gatewayRestartAttempted: boolean(),
	gatewayVisibility: openMeldDaemonTaskResultOpenClawGatewayVisibilityStatusSchema
});
const openMeldDaemonTaskResultOpenClawBindingClassificationSchema = enumFactory([
	"missing",
	"canonical_owned",
	"legacy",
	"foreign"
]);
const openMeldDaemonTaskResultOpenClawBindingSchema = strictObject({ classification: openMeldDaemonTaskResultOpenClawBindingClassificationSchema });
const openMeldDaemonTaskResultLocalOwnerConflictSchema = strictObject({
	activeAttemptId: string().min(1).nullable(),
	activeDispatchId: string().min(1).nullable(),
	activeExecutionTokenRunId: string().min(1).nullable(),
	activeRuntimeCoordinatorExecutionId: string().min(1).nullable(),
	activeTaskId: string().min(1).nullable(),
	reason: literal("task.owner_conflict"),
	rejectedAttemptId: string().min(1),
	rejectedDispatchId: string().min(1),
	rejectedExecutionTokenRunId: string().min(1).nullable(),
	rejectedRuntimeCoordinatorExecutionId: string().min(1).nullable(),
	rejectedTaskId: string().min(1)
});
const openMeldDaemonTaskResultPromptTraceSchema = strictObject({
	fingerprint: string().min(1),
	length: int().nonnegative(),
	preview: union([string().min(1), nullFactory()])
});
const openMeldDaemonTaskResultClaimLeaseSchema = strictObject({
	claimLeaseId: string().min(1),
	outcome: enumFactory(["existing_lease", "new_lease"]),
	status: literal("accepted")
});
const openMeldDaemonTaskResultProjectFailureContextSchema = strictObject({
	bindingGeneration: string().min(1).optional(),
	projectId: string().min(1)
});
const openMeldDaemonTaskResultTokenUsageBreakdownSchema = strictObject({
	cacheWriteInputTokens: int().nonnegative().optional(),
	cachedInputTokens: int().nonnegative(),
	inputTokens: int().nonnegative(),
	outputTokens: int().nonnegative(),
	reasoningOutputTokens: int().nonnegative(),
	totalTokens: int().nonnegative()
});
const openMeldDaemonTaskResultTokenUsageSchema = strictObject({
	last: openMeldDaemonTaskResultTokenUsageBreakdownSchema,
	modelContextWindow: int().nonnegative().nullable(),
	total: openMeldDaemonTaskResultTokenUsageBreakdownSchema
});
const openMeldDaemonTaskResultTelemetrySchema = strictObject({
	completedAtMs: number().int().nonnegative(),
	contextCompactionObserved: boolean().optional(),
	contextHealed: boolean().optional(),
	exitCode: int().nullable().optional(),
	exitObservedAtMs: number().int().nonnegative().optional(),
	exitSignal: string().min(1).nullable().optional(),
	firstOutputAtMs: number().int().nonnegative().optional(),
	firstStderrAtMs: number().int().nonnegative().optional(),
	firstStdoutAtMs: number().int().nonnegative().optional(),
	invokedAtMs: number().int().nonnegative(),
	previewEmitted: boolean().optional(),
	processId: number().int().positive().optional(),
	processSpawnedAtMs: number().int().nonnegative().optional(),
	resumedContext: boolean(),
	stdinClosedAtMs: number().int().nonnegative().optional(),
	streamingMode: enumFactory(["final", "stream"]).optional(),
	timeoutTriggeredAtMs: number().int().nonnegative().optional(),
	tokenUsage: openMeldDaemonTaskResultTokenUsageSchema.optional()
});
const openMeldDaemonTaskResultCommonSchemaV1 = strictObject({
	taskId: string().min(1),
	sourceEventId: string().min(1),
	agentId: string().min(1),
	deduped: boolean(),
	agentControllerRef: string().min(1).optional(),
	agentControllerConversationId: string().min(1).optional()
});
const openMeldDaemonTaskResultCommonSchema = openMeldDaemonTaskResultCommonSchemaV1.extend({
	executionContextId: string().uuid().optional(),
	executionContextCompletion: executionContextCompletionSchema.optional(),
	attemptId: string().min(1),
	telemetry: openMeldDaemonTaskResultTelemetrySchema.optional(),
	agentControllerConversation: agentControllerConversationRefSchema.optional(),
	effectiveRuntimeWorkingDirectory: profileWorkingDirectorySchema.optional(),
	runtimeWorkspaceFingerprint: string().min(1).optional(),
	transportMode: runtimeTransportModeSchema.optional(),
	promptTrace: openMeldDaemonTaskResultPromptTraceSchema.optional(),
	projectFailureContext: openMeldDaemonTaskResultProjectFailureContextSchema.optional(),
	claimLease: openMeldDaemonTaskResultClaimLeaseSchema.optional(),
	bootstrapTemplateFacts: bootstrapTemplateFactsSchema.optional(),
	dispatchExecution: dispatchExecutionSchema.optional(),
	openClawGatewayVisibility: openMeldDaemonTaskResultOpenClawGatewayVisibilitySchema.optional(),
	openClawBinding: openMeldDaemonTaskResultOpenClawBindingSchema.optional(),
	localOwnerConflict: openMeldDaemonTaskResultLocalOwnerConflictSchema.optional(),
	spaceAction: openMeldSpaceEgressActionSchema.optional(),
	spaceActionFailureDiagnostics: openMeldDaemonTaskResultSpaceActionFailureDiagnosticsSchema.optional(),
	spaceActionProvenance: openMeldDaemonTaskResultSpaceActionProvenanceSchema.optional(),
	suppressPublicOutcome: boolean().optional()
});
const openMeldDaemonTaskResultCommonSchemaV3 = openMeldDaemonTaskResultCommonSchemaV1.extend({
	attemptId: string().min(1),
	telemetry: openMeldDaemonTaskResultTelemetrySchema.optional(),
	agentControllerConversation: agentControllerConversationRefSchema.optional(),
	effectiveRuntimeWorkingDirectory: profileWorkingDirectorySchema.optional(),
	runtimeWorkspaceFingerprint: string().min(1).optional(),
	bootstrapTemplateFacts: bootstrapTemplateFactsSchema.optional(),
	dispatchExecution: dispatchExecutionSchema.optional(),
	openClawGatewayVisibility: openMeldDaemonTaskResultOpenClawGatewayVisibilitySchema.optional(),
	openClawBinding: openMeldDaemonTaskResultOpenClawBindingSchema.optional(),
	spaceAction: openMeldSpaceEgressActionSchema.optional(),
	suppressPublicOutcome: boolean().optional()
});
const openMeldDaemonTaskResultPayloadSchema = discriminatedUnion("status", [
	openMeldDaemonTaskResultCommonSchema.extend({
		status: literal("success"),
		output: string().min(1)
	}),
	openMeldDaemonTaskResultCommonSchema.extend({
		status: literal("partial_success"),
		output: string().min(1),
		errorType: openMeldDaemonTaskResultErrorTypeSchema,
		errorCode: string().min(1).optional(),
		partialReason: string().min(1),
		errorMessage: string().min(1).optional()
	}),
	openMeldDaemonTaskResultCommonSchema.extend({
		status: literal("failed"),
		errorType: openMeldDaemonTaskResultErrorTypeSchema,
		errorCode: string().min(1).optional(),
		errorMessage: string().min(1).optional(),
		originalError: openMeldDaemonOriginalErrorSchema.optional(),
		providerAccessEvidence: openMeldDaemonProviderAccessEvidenceSchema.optional()
	})
]).superRefine((value, ctx) => {
	const isProjectConnectionFailure = value.status === "failed" && projectLocalConnectionFailureReasonCodeSchema.safeParse(value.errorCode).success;
	if (isProjectConnectionFailure && !value.projectFailureContext) ctx.addIssue({
		code: "custom",
		message: "Project connection failures must include path-free Project trace context",
		path: ["projectFailureContext"]
	});
	if (!isProjectConnectionFailure && value.projectFailureContext) ctx.addIssue({
		code: "custom",
		message: "Project trace context is only valid for Project connection failures",
		path: ["projectFailureContext"]
	});
});
const openMeldDaemonTaskResultPayloadSchemaV3 = discriminatedUnion("status", [
	openMeldDaemonTaskResultCommonSchemaV3.extend({
		status: literal("success"),
		output: string().min(1)
	}),
	openMeldDaemonTaskResultCommonSchemaV3.extend({
		status: literal("partial_success"),
		output: string().min(1),
		errorType: openMeldDaemonTaskResultErrorTypeSchema,
		errorCode: string().min(1).optional(),
		partialReason: string().min(1),
		errorMessage: string().min(1).optional()
	}),
	openMeldDaemonTaskResultCommonSchemaV3.extend({
		status: literal("failed"),
		errorType: openMeldDaemonTaskResultErrorTypeSchema,
		errorCode: string().min(1).optional(),
		errorMessage: string().min(1).optional()
	})
]);
const openMeldDaemonTaskResultPayloadSchemaV1 = discriminatedUnion("status", [
	openMeldDaemonTaskResultCommonSchemaV1.extend({
		status: literal("success"),
		output: string().min(1)
	}),
	openMeldDaemonTaskResultCommonSchemaV1.extend({
		status: literal("partial_success"),
		output: string().min(1),
		errorType: openMeldDaemonTaskResultErrorTypeSchema,
		errorCode: string().min(1).optional(),
		partialReason: string().min(1),
		errorMessage: string().min(1).optional()
	}),
	openMeldDaemonTaskResultCommonSchemaV1.extend({
		status: literal("failed"),
		errorType: openMeldDaemonTaskResultErrorTypeSchema,
		errorCode: string().min(1).optional(),
		errorMessage: string().min(1).optional()
	})
]);
const openMeldDaemonTaskResultEnvelopeWriteSchema = strictObject({
	v: literal(4),
	code: literal("service.task_result"),
	payload: openMeldDaemonTaskResultPayloadSchema
});
const openMeldDaemonTaskResultEnvelopeSchemaV3 = strictObject({
	v: literal(OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION_PREVIOUS),
	code: literal("service.task_result"),
	payload: openMeldDaemonTaskResultPayloadSchemaV3
});
const openMeldDaemonTaskResultEnvelopeSchema = union([
	openMeldDaemonTaskResultEnvelopeWriteSchema,
	strictObject({
		v: literal(OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION_LEGACY),
		code: literal("service.task_result"),
		payload: openMeldDaemonTaskResultPayloadSchemaV3
	}),
	openMeldDaemonTaskResultEnvelopeSchemaV3,
	strictObject({
		v: literal(OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION_OLDEST),
		code: literal("service.task_result"),
		payload: openMeldDaemonTaskResultPayloadSchemaV1
	})
]);
//#endregion
export { OPENMELD_DAEMON_TASK_RESULT_SCHEMA_VERSION, OPENMELD_ORIGINAL_ERROR_MAX_LENGTH, isOriginalErrorSafeForMessageDetails, openMeldDaemonOriginalErrorSchema, openMeldDaemonProviderAccessEvidenceProviderSchema, openMeldDaemonProviderAccessEvidenceReasonCodeSchema, openMeldDaemonProviderAccessEvidenceSchema, openMeldDaemonProviderAccessEvidenceSourceSchema, openMeldDaemonTaskResultClaimLeaseSchema, openMeldDaemonTaskResultEnvelopeSchema, openMeldDaemonTaskResultEnvelopeWriteSchema, openMeldDaemonTaskResultErrorTypeSchema, openMeldDaemonTaskResultLocalOwnerConflictSchema, openMeldDaemonTaskResultOpenClawBindingClassificationSchema, openMeldDaemonTaskResultOpenClawBindingSchema, openMeldDaemonTaskResultOpenClawGatewayVisibilitySchema, openMeldDaemonTaskResultOpenClawGatewayVisibilityStatusSchema, openMeldDaemonTaskResultPayloadSchema, openMeldDaemonTaskResultProjectFailureContextSchema, openMeldDaemonTaskResultPromptTraceSchema, openMeldDaemonTaskResultSpaceActionCarrierKindSchema, openMeldDaemonTaskResultSpaceActionFailureDiagnosticsSchema, openMeldDaemonTaskResultSpaceActionFailureRepairModeSchema, openMeldDaemonTaskResultSpaceActionProvenanceSchema, openMeldDaemonTaskResultStatusSchema, openMeldDaemonTaskResultTelemetrySchema, openMeldDaemonTaskResultTokenUsageSchema };

//# sourceMappingURL=task-result.js.map